import Button from '@atlaskit/button';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { colors, fontFamily } from '@atlaskit/theme';
import React from 'react';
import styled from 'styled-components';

import { UserAvatar } from './components/user-avatar';
import { EmptyView } from './components/empty-view';
import { ProductDropdownItemProps } from './components/product-dropdown-item';
import { ProductsView } from './components/products-view';
import { UserNav } from './components/user-nav';

export type UserProfileViewType = 'wac-desktop' | 'wac-mobile';

export type UserProfileViewProps = {
  readonly userName: string;
  readonly userEmail: string;
  readonly products: ProductDropdownItemProps[];

  readonly onSwitchAccountClick: () => void;
  readonly onProfileClick: () => void;
  readonly onLicensesClick: () => void;
  readonly onLogOutClick: () => void;

  readonly getStartedUrl: string;
  readonly onGetStartedClick?: () => void;
  readonly onMobileBackClick?: () => void;
  readonly onMobileCloseClick?: () => void;

  readonly avatarSrc?: string;
  readonly type?: UserProfileViewType;
  readonly domRootId?: string;
};

export function UserProfileView({
  userName,
  userEmail,
  products,
  getStartedUrl,
  onGetStartedClick,
  onSwitchAccountClick,
  onProfileClick,
  onLicensesClick,
  onLogOutClick,
  onMobileBackClick,
  onMobileCloseClick,
  domRootId,
  avatarSrc,
  type = 'wac-desktop'
}: UserProfileViewProps) {
  switch (type) {
    case 'wac-mobile':
      return (
        <RootContainerMobile>
          <TopRowContainer>
            <Button
              appearance={'subtle-link'}
              iconAfter={<ChevronLeftLargeIcon label="back" />}
              onClick={() => onMobileBackClick && onMobileBackClick()}
            />
            <UserDetailsContainerMobile>
              <UserNameLabelMobile>{userName}</UserNameLabelMobile>
              <UserEmailLabelMobile>{userEmail}</UserEmailLabelMobile>
            </UserDetailsContainerMobile>
            <Button
              appearance={'subtle-link'}
              iconAfter={<CrossIcon label="close" />}
              onClick={() => onMobileCloseClick && onMobileCloseClick()}
            />
          </TopRowContainer>
          {products.length > 0 ? (
            <ProductsView
              products={products}
              type={type}
              domRootId={domRootId}
            />
          ) : (
            <EmptyView
              getStartedUrl={getStartedUrl}
              onGetStartedClick={onGetStartedClick}
            />
          )}
          <FooterMobile>
            <UserNav
              onProfileClick={onProfileClick}
              onLicensesClick={onLicensesClick}
            />
          </FooterMobile>
        </RootContainerMobile>
      );
    default:
      return (
        <RootContainer>
          <UserDetailsContainer>
            <UserAvatar avatarSrc={avatarSrc} userName={userName} />
            <UserInfoContainer>
              <UserNameLabel>{userName}</UserNameLabel>
              <UserEmailLabel>{userEmail}</UserEmailLabel>
            </UserInfoContainer>
          </UserDetailsContainer>

          {products.length > 0 ? (
            <ProductsView products={products} domRootId={domRootId} />
          ) : (
            <EmptyView
              getStartedUrl={getStartedUrl}
              onGetStartedClick={onGetStartedClick}
            />
          )}
          <Footer>
            <UserNav
              onSwitchAccountClick={onSwitchAccountClick}
              onProfileClick={onProfileClick}
              onLicensesClick={onLicensesClick}
              onLogOutClick={onLogOutClick}
            />
          </Footer>
        </RootContainer>
      );
  }
}

const RootContainer = styled.div`
  width: 190px;
  margin: -4px 0;
  font-family: ${fontFamily};
`;

const RootContainerMobile = styled.div`
  width: 100%;
  margin: -4px 0;
  font-family: ${fontFamily};
`;

const UserDetailsContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 8px 14px;
  background-color: ${colors.N800};
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-flow: column;
  margin-left: 8px;
`;

const UserDetailsContainerMobile = styled.div`
  flex: 1;
  padding: 8px 14px;
  color: white;
`;

const Footer = styled.div`
  margin: 12px;
  padding-top: 11px;
  border-top: 1px solid ${colors.N40};
`;

const FooterMobile = styled.div`
  padding: 20px 15px;
  border-top: 1px solid ${colors.N40};
  background-color: ${colors.N20};
`;

const UserNameLabel = styled.div`
  font-size: 15px;
  line-height: 18px;
  color: ${colors.N0};
`;

const UserEmailLabel = styled.div`
  font-size: 10px;
  line-height: 12px;
  color: ${colors.N50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserNameLabelMobile = styled.div`
  font-size: 15px;
  line-height: 18px;
  color: black;
`;

const UserEmailLabelMobile = styled.div`
  font-size: 10px;
  line-height: 12px;
  color: black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopRowContainer = styled.div`
  width: 100%;
  display: flex;
  border-bottom: 1px solid ${colors.N40};
`;
